/**
 * The public pitch (architecture §6/§7), as a long-scroll explainer: hero, the
 * lateral-move thesis, two product sections carrying screenshots, the real
 * board list, a trust strip, one closing CTA.
 *
 * Static and SEO-friendly — React 19 hoists the `<title>`/`<meta>` tags
 * rendered below into `<head>` automatically, no extra library needed.
 *
 * Two traps this page had, and is now shaped to avoid:
 *   - `SignInCta` was rendered twice in full, so the email fallback was offered
 *     twice. That is most of why the page read as repeating itself. Only the
 *     hero passes `emailFallback`; the closing CTA is the Google button alone.
 *   - "Dare to change" was both step 3's heading *and* the closing heading.
 *     It is the closing heading, once.
 */

import { Link, Navigate } from 'react-router'
import { useAuth } from '../auth/AuthContext'
import { LangSwitch } from '../components/LangSwitch'
import { PivotLogo } from '../components/PivotLogo'
import { SignInCta } from '../components/SignInCta'
import { ThemeToggle } from '../components/ThemeToggle'
import { useI18n } from '../i18n'
import type { TranslationKey } from '../i18n/types'
import type { Locale } from '../types/domain'
import shotDashboardEnLight from '../assets/shot-dashboard-en-light.png'
import shotDashboardEnDark from '../assets/shot-dashboard-en-dark.png'
import shotDashboardFrLight from '../assets/shot-dashboard-fr-light.png'
import shotDashboardFrDark from '../assets/shot-dashboard-fr-dark.png'
import shotDashboardNlLight from '../assets/shot-dashboard-nl-light.png'
import shotDashboardNlDark from '../assets/shot-dashboard-nl-dark.png'
import shotInterviewEnLight from '../assets/shot-interview-en-light.png'
import shotInterviewEnDark from '../assets/shot-interview-en-dark.png'
import shotInterviewFrLight from '../assets/shot-interview-fr-light.png'
import shotInterviewFrDark from '../assets/shot-interview-fr-dark.png'
import shotInterviewNlLight from '../assets/shot-interview-nl-light.png'
import shotInterviewNlDark from '../assets/shot-interview-nl-dark.png'
import shotMatchEnLight from '../assets/shot-match-en-light.png'
import shotMatchEnDark from '../assets/shot-match-en-dark.png'
import shotMatchFrLight from '../assets/shot-match-fr-light.png'
import shotMatchFrDark from '../assets/shot-match-fr-dark.png'
import shotMatchNlLight from '../assets/shot-match-nl-light.png'
import shotMatchNlDark from '../assets/shot-match-nl-dark.png'
import '../styles/header-actions.css'
import './Landing.css'

/** Aspect ratios `Landing.css` knows how to frame. */
type ShotRatio = '16x10' | '4x3' | '3x2'

/** One shot's light/dark capture for one `Locale` — `scripts/capture-shots.mjs`
 * generates all six combinations for each of the three call sites below. */
interface ShotVariant {
  light: string
  dark: string
}

/** Built once per shot from the locale-keyed import pairs above, so a call
 * site only has to say which shot and which locale — `Shot` itself decides
 * light vs. dark by CSS (see `data-theme-variant` below), not by picking an
 * import in JS, since nothing in this file is re-rendered when `ThemeToggle`
 * flips `data-theme`. */
function shotSet(en: ShotVariant, fr: ShotVariant, nl: ShotVariant): Record<Locale, ShotVariant> {
  return { en, fr, nl }
}

const DASHBOARD_SHOTS = shotSet(
  { light: shotDashboardEnLight, dark: shotDashboardEnDark },
  { light: shotDashboardFrLight, dark: shotDashboardFrDark },
  { light: shotDashboardNlLight, dark: shotDashboardNlDark },
)
const INTERVIEW_SHOTS = shotSet(
  { light: shotInterviewEnLight, dark: shotInterviewEnDark },
  { light: shotInterviewFrLight, dark: shotInterviewFrDark },
  { light: shotInterviewNlLight, dark: shotInterviewNlDark },
)
const MATCH_SHOTS = shotSet(
  { light: shotMatchEnLight, dark: shotMatchEnDark },
  { light: shotMatchFrLight, dark: shotMatchFrDark },
  { light: shotMatchNlLight, dark: shotMatchNlDark },
)

interface ShotProps {
  /**
   * The locale-resolved `{ light, dark }` pair for this call site (e.g.
   * `DASHBOARD_SHOTS[locale]`). Until a real pair exists the frame renders
   * its caption instead of a blank box, so the gap is legible rather than
   * looking like a broken image.
   */
  variant?: ShotVariant
  /** Doubles as the image's `alt` — it describes what is on screen. */
  captionKey: TranslationKey
  ratio: ShotRatio
  /** Tilt direction. `center` sits square, which is what the hero wants. */
  lean: 'left' | 'right' | 'center'
}

/** Named export purely so `Landing.test.tsx` can exercise the `variant`-absent
 * branch directly — every real call site below always passes one now
 * (PIVOT-174), so that branch is no longer reachable through a full render
 * of the page. */
export function Shot({ variant, captionKey, ratio, lean }: ShotProps) {
  const { t } = useI18n()
  return (
    <figure
      className="landing-shot"
      data-ratio={ratio}
      data-lean={lean}
      // PIVOT-174: all three call sites now pass a real `variant` (PNGs
      // `scripts/capture-shots.mjs` generated from the actual components —
      // see `pages/__shots/`). This branch is what stays behind: `variant` is
      // absent again only if a build ever ships without the assets, and
      // `data-pending` drops `Landing.css`'s aspect-ratio box to a short
      // strip rather than reserving a full 16x10/4x3/3x2 frame for nothing —
      // that failure mode is legible instead of three empty rectangles
      // silently claiming to be screenshots (constitution §I).
      data-pending={variant ? undefined : 'true'}
    >
      {variant ? (
        <>
          {/* Both are always in the DOM; `Landing.css`'s `data-theme-variant`
              rules show exactly one, off the same `data-theme` attribute
              `ThemeToggle` writes — so flipping the toggle swaps the shot
              instantly, with no re-render and no second network request. */}
          <img
            className="landing-shot-img"
            data-theme-variant="light"
            src={variant.light}
            alt={t(captionKey)}
          />
          <img
            className="landing-shot-img"
            data-theme-variant="dark"
            src={variant.dark}
            alt={t(captionKey)}
          />
        </>
      ) : (
        <span className="landing-shot-pending">{t(captionKey)}</span>
      )}
    </figure>
  )
}

/**
 * The from → to pairs that carry the product's whole idea. Illustrative, and
 * labelled as such on the page — inventing a testimonial or a real user's move
 * would be the padding the product exists not to do (constitution §I).
 *
 * Keys stay written out rather than built as `landing.turnFrom${n}`, so they
 * remain greppable and statically checkable.
 */
const TURNS: readonly { fromKey: TranslationKey; toKey: TranslationKey }[] = [
  { fromKey: 'landing.turnFrom1', toKey: 'landing.turnTo1' },
  { fromKey: 'landing.turnFrom2', toKey: 'landing.turnTo2' },
  { fromKey: 'landing.turnFrom3', toKey: 'landing.turnTo3' },
]

/**
 * The boards Pivot actually reads, mirroring `_BOARD_CLASSES` in
 * `backend/src/pivot/scrapers/registry.py`. Domains rather than the registry's
 * slugs, because a Belgian reader recognises the domain.
 *
 * Not `t()` keys: these are proper nouns and read identically in all three
 * locales, so routing them through the dictionaries would add 15 keys × 3 files
 * that can only ever drift apart.
 *
 * `jobs.uclouvain.be` and `jobs.vub.be` are in the registry but have never
 * yielded an offer, so they are not claimed here. This list only ever shrinks
 * toward the registry, never past it — a board appears here after it produces.
 */
// Grouped by the language a board publishes in, which is what the labels claim
// and what `backend/src/pivot/scrapers/boards/<locale>/` tracks. Only boards
// that have returned a live offer are listed: `uclouvain.be` and `vub.ac.be` are
// registered and have never yielded one, so putting them here would be a
// coverage claim we can't back (PIVOT-016, constitution §I).
const BOARDS = {
  fr: [
    'guidesocial.be',
    'alterjob.be',
    'economiesociale.be',
    'acodev.be',
    'culture.be',
    'kbs-frb.be',
    'meet-my-job.com',
    'talent.brussels',
    'travaillerpour.be',
    'ictjob.be',
    'lexgo.be',
    'uvcw.be',
    'jobecole.be',
    'sciensano.be',
    'ulb.ac.be',
    'uliege.be',
  ],
  nl: [
    'vlaanderen.be',
    'engineering.jobs',
    'brightplus.be',
    'imec.be',
    '11.be',
    'sociaalwerkjobs.be',
    'cultuurjobs.be',
    'vlaamsehogescholenraad.be',
    'aviato.be',
    'bondbeterleefmilieu.be',
    'vito.be',
    'kuleuven.be',
    'ugent.be',
    'uantwerpen.be',
  ],
  de: ['adg.be'],
  en: [
    'euraxess.ec.europa.eu',
    'eu-careers.europa.eu',
    'eurobrussels.com',
    'brusselsjobs.com',
    'jobsin.brussels',
    'euractiv.com',
  ],
} as const

export default function Landing() {
  const { locale, t } = useI18n()
  const { user } = useAuth()

  // Branch on the real state instead of sending everyone to `/dashboard` and
  // letting `RequireOnboarded` bounce the unonboarded back — that hop was
  // visible, and it is the same unconditional redirect PIVOT-070 removed from
  // the login and signup pages.
  if (user) return <Navigate to={user.profile_complete ? '/dashboard' : '/onboarding'} replace />

  return (
    <div className="landing">
      <title>{`${t('common.appName')} — ${t('landing.heroTitle')}`}</title>
      <meta name="description" content={t('landing.metaDescription')} />
      <meta property="og:title" content={t('landing.heroTitle')} />
      <meta property="og:description" content={t('landing.metaDescription')} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />

      <div className="landing-bar">
        <span className="landing-brand">
          <PivotLogo />
          {t('common.appName')}
        </span>
        <div className="header-actions">
          <LangSwitch />
          <ThemeToggle />
        </div>
      </div>

      <header className="landing-hero">
        <h1 className="landing-hero-title">{t('landing.heroTitle')}</h1>
        <p className="landing-subtitle">{t('landing.subtitle')}</p>
        <SignInCta />
        {/* Google first, with no explanation, on a page that is about to ask
            for a CV reads as friction. This says what the button actually
            grants: the OAuth scope requested in `GoogleSignInButton` is
            `openid email profile` and nothing more, so the claim is checkable. */}
        <p className="landing-google-note">{t('landing.googleNote')}</p>
        <a className="landing-cue" href="#how">
          {t('landing.scrollCue')}
        </a>
        <div className="landing-hero-shot">
          <Shot
            variant={DASHBOARD_SHOTS[locale]}
            captionKey="landing.shotDashboard"
            ratio="16x10"
            lean="center"
          />
        </div>
      </header>

      <section className="landing-turn landing-reveal">
        {/* PIVOT-144: was a `<p>` — the only way a screen-reader user
            navigating by heading ever reached the product's central claim.
            `.eyebrow` overrides family/size/weight/colour regardless of tag,
            so this renders identically to before. */}
        <h2 className="eyebrow">{t('landing.turnEyebrow')}</h2>
        <div className="landing-turn-stack">
          {TURNS.map((turn) => (
            <p key={turn.fromKey} className="landing-turn-pair">
              <span className="landing-turn-from">{t(turn.fromKey)}</span>
              <span className="landing-turn-arc" aria-hidden="true" />
              <span className="landing-turn-to">{t(turn.toKey)}</span>
            </p>
          ))}
        </div>
        <p className="landing-turn-note">{t('landing.turnNote')}</p>
      </section>

      <section className="landing-row landing-reveal" id="how" data-media="right">
        <div className="landing-row-copy">
          <p className="eyebrow">{t('landing.interviewEyebrow')}</p>
          <h2>{t('landing.interviewTitle')}</h2>
          <p className="landing-row-body">{t('landing.interviewBody')}</p>
        </div>
        <Shot
          variant={INTERVIEW_SHOTS[locale]}
          captionKey="landing.shotInterview"
          ratio="4x3"
          lean="right"
        />
      </section>

      <section className="landing-row landing-reveal" data-media="left">
        <div className="landing-row-copy">
          <p className="eyebrow">{t('landing.rankEyebrow')}</p>
          <h2>{t('landing.rankTitle')}</h2>
          <p className="landing-row-body">{t('landing.rankBody')}</p>
          <p className="landing-row-honest">{t('landing.rankHonest')}</p>
        </div>
        <Shot variant={MATCH_SHOTS[locale]} captionKey="landing.shotMatch" ratio="3x2" lean="left" />
      </section>

      <section className="landing-sources landing-reveal">
        <div className="landing-row-copy">
          <p className="eyebrow">{t('landing.sourcesEyebrow')}</p>
          <h2>{t('landing.sourcesTitle')}</h2>
          <p className="landing-row-body">{t('landing.sourcesBody')}</p>
          <p className="landing-sources-aside">{t('landing.sourcesAside')}</p>
        </div>
        <div className="landing-boards">
          <div>
            <p className="landing-boards-label">{t('landing.sourcesFrLabel')}</p>
            <ul className="landing-boards-list">
              {BOARDS.fr.map((board) => (
                <li key={board}>{board}</li>
              ))}
            </ul>
          </div>
          <div>
            <p className="landing-boards-label">{t('landing.sourcesNlLabel')}</p>
            <ul className="landing-boards-list">
              {BOARDS.nl.map((board) => (
                <li key={board}>{board}</li>
              ))}
            </ul>
          </div>
          <div>
            <p className="landing-boards-label">{t('landing.sourcesDeLabel')}</p>
            <ul className="landing-boards-list">
              {BOARDS.de.map((board) => (
                <li key={board}>{board}</li>
              ))}
            </ul>
          </div>
          <div>
            <p className="landing-boards-label">{t('landing.sourcesEnLabel')}</p>
            <ul className="landing-boards-list">
              {BOARDS.en.map((board) => (
                <li key={board}>{board}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="landing-trust">
        {/* PIVOT-144: the other `<section>` with no heading. */}
        <h2 className="eyebrow landing-trust-heading">{t('landing.trustHeading')}</h2>
        <ul className="landing-trust-list">
          <li>{t('landing.trustLangs')}</li>
          <li>{t('landing.trustConsent')}</li>
          <li>{t('landing.trustErasure')}</li>
          <li>{t('landing.trustResidency')}</li>
        </ul>
      </section>

      <section className="landing-close landing-reveal">
        <h2>{t('landing.closeTitle')}</h2>
        <p className="landing-close-body">{t('landing.closeBody')}</p>
        <SignInCta emailFallback={false} />
      </section>

      <footer className="landing-footer">
        <p>
          © {new Date().getFullYear()} {t('common.appName')} · {t('landing.footerRights')} ·{' '}
          <Link to="/privacy" className="landing-footer-link">
            {t('landing.footerPrivacy')}
          </Link>
        </p>
      </footer>
    </div>
  )
}
